import { useEffect, useRef, useState } from 'react'
import { endpoints, api, ApiError, type Account } from '../api/client'
import {
  mountGoogleButton, signInWithApple, signInWithFacebook, preloadFacebook, isCancelled,
} from '../api/socialAuth'
import { Mark } from '../components/Mark'
import { isNativeApp } from '../native'

/*
  One card, three modes: sign in, create an account, and ask for a reset link.

  They share a card rather than living on separate routes because the landing page hands
  all three the same navy panel beside them, and because the common case for a signed-out
  visitor is that they are not sure which of the three they need. Switching is a state
  change, so the email survives the switch: somebody who typed an address, found out it was
  already taken and moved to the reset form should not have to type it again.

  Google appears only once the server reports a client id, because a button that cannot
  work is worse than no button.
*/

/*
  'finish' is the step a provider forces on us. Google and Apple can say who somebody is;
  neither can say what their business is called or what trade it is in, and a board cannot
  be built without both. So a first-time social sign-in lands here holding a verified token
  and asks the two questions, rather than inventing answers.
*/
type Mode = 'signin' | 'signup' | 'forgot' | 'finish'

type Provider = 'google' | 'apple' | 'facebook'

export function SignIn({ onSignedIn }: { onSignedIn: (a: Account) => void }) {
  const [mode, setMode] = useState<Mode>('signin')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [company, setCompany] = useState('')
  const [trade, setTrade] = useState('')
  const [name, setName] = useState('')

  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Set when the server refuses a correct password because the address is unconfirmed.
  // That is the one failure worth offering a way out of, so it gets its own state.
  const [unverified, setUnverified] = useState(false)
  const [google, setGoogle] = useState<{ clientId: string } | null>(null)
  const [apple, setApple] = useState<{ servicesId: string } | null>(null)
  const [facebook, setFacebook] = useState<{ appId: string; version: string } | null>(null)

  // The verified provider token, held only for as long as it takes to ask for the business
  // name and trade. The server re-verifies it on the signup call rather than trusting that
  // this ever happened.
  const [pending, setPending] = useState<{ provider: Provider; idToken: string } | null>(null)
  const googleSlot = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    void api.get<{
      google: { clientId: string } | null
      apple: { servicesId: string } | null
      facebook: { appId: string; version: string } | null
    }>('/auth/config')
      .then(c => {
        setGoogle(c.google)
        setApple(c.apple)
        setFacebook(c.facebook)

        /*
          Fetched now rather than on the click. Facebook's dialog is a popup and a popup
          only opens inside the gesture that asked for it, so loading the SDK from the
          click handler spends the gesture and the browser blocks the window.

          A failure here is deliberately silent: the button reports it when pressed, and
          an error on a sign-in card about a library nobody has asked for yet is noise.
        */
        if (c.facebook) void preloadFacebook(c.facebook.appId, c.facebook.version).catch(() => {})
      })
      .catch(() => { setGoogle(null); setApple(null); setFacebook(null) })
  }, [])

  /*
    Google draws its own button, so it can only be mounted once the container exists. The
    slot is absent in 'forgot' and 'finish', hence the guard: rendering into a node that is
    not on the page silently does nothing and leaves no button behind when it returns.
  */
  useEffect(() => {
    const slot = googleSlot.current
    if (!google || !slot) return

    /*
      Google is handed a pixel width, so the button has to be drawn against a slot that has
      actually been laid out, and redrawn if that measurement ever changes.

      Measuring once on mount is not enough and was wrong in both directions. This card
      arrives inside the landing page's auth panel, so on the first pass the slot is still
      zero wide and every button came out at Google's 200 floor. A phone rotated later would
      have kept whatever width it was born with while the inputs above it reflowed.

      Hence the observer rather than a resize listener: it fires on the first real layout as
      well as on rotation. Debounced so a drag redraws once it settles, and skipped when the
      width has not moved, because renderButton replaces the slot's children and would
      otherwise rebuild the button every time only the height changed.
    */
    let timer: number | undefined
    let drawn = 0

    const draw = () => {
      const width = slot.getBoundingClientRect().width
      if (width < 1 || Math.abs(width - drawn) < 1) return
      drawn = width

      void mountGoogleButton(slot, google.clientId, token => { void useProviderToken('google', token) })
        .catch(() => setError('Google sign-in could not load.'))
    }

    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer)
      timer = window.setTimeout(draw, 120)
    })

    observer.observe(slot)
    draw()

    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google, mode])

  /**
   * One verified token, two possible outcomes: straight in, or the two questions first.
   */
  const useProviderToken = async (provider: Provider, idToken: string) => {
    setBusy(true)
    setError(null)
    setNote(null)

    try {
      onSignedIn(await endpoints.socialLogin(provider, idToken))
    } catch (err) {
      const needsAccount =
        err instanceof ApiError && err.status === 404 &&
        (err.body as { needsAccount?: boolean } | undefined)?.needsAccount === true

      if (needsAccount) {
        setPending({ provider, idToken })
        setMode('finish')
      } else {
        setError(err instanceof Error ? err.message : 'That sign-in did not work.')
      }
    } finally {
      setBusy(false)
    }
  }

  const startApple = async () => {
    if (!apple || busy) return
    setBusy(true)
    setError(null)

    try {
      const token = await signInWithApple(apple.servicesId)
      setBusy(false)
      await useProviderToken('apple', token)
    } catch (err) {
      setBusy(false)
      // Closing the popup is a decision, not a failure.
      if (!isCancelled(err)) {
        setError(err instanceof Error ? err.message : 'Apple sign-in did not work.')
      }
    }
  }

  const startFacebook = async () => {
    if (!facebook || busy) return
    setBusy(true)
    setError(null)

    try {
      const token = await signInWithFacebook()
      setBusy(false)
      // Not an id_token. It is an access token, and only Graph can say what it means, which
      // is why the server does the asking rather than this card reading anything off it.
      await useProviderToken('facebook', token)
    } catch (err) {
      setBusy(false)
      if (!isCancelled(err)) {
        setError(err instanceof Error ? err.message : 'Facebook sign-in did not work.')
      }
    }
  }

  const go = (next: Mode) => {
    setMode(next)
    setError(null)
    setNote(null)
    setUnverified(false)
  }

  const resend = async () => {
    setBusy(true)
    try {
      setNote((await endpoints.resendVerification(email)).message)
      setError(null)
      setUnverified(false)
    } catch {
      setError('Could not send that. Try again in a moment.')
    } finally {
      setBusy(false)
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    setNote(null)
    setUnverified(false)

    try {
      if (mode === 'signin') {
        onSignedIn(await endpoints.login(email, password))
      } else if (mode === 'signup') {
        onSignedIn(await endpoints.signup({
          email,
          password,
          company,
          trade,
          name: name.trim() || undefined,
          // The board meets on a weekday morning in the owner's own time, so the browser's
          // zone is a far better default than the server's.
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
        }))
      } else if (mode === 'finish' && pending) {
        onSignedIn(await endpoints.socialSignup({
          provider: pending.provider,
          idToken: pending.idToken,
          company,
          trade,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
        }))
      } else {
        setNote((await endpoints.forgot(email)).message)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong'
      setError(message)
      // Matched on the server's own wording rather than the status code, because the API
      // client collapses everything non-2xx into one Error.
      if (/confirm your email/i.test(message)) setUnverified(true)
    } finally {
      setBusy(false)
    }
  }

  const cta = mode === 'signin' ? 'Sign in'
    : mode === 'signup' ? 'Create the account'
    : mode === 'finish' ? 'Build my board'
    : 'Send the link'

  const heading = mode === 'signin' ? 'Sign in'
    : mode === 'signup' ? 'Start a board'
    : mode === 'finish' ? 'Two more things'
    : 'Reset your password'

  const working = mode === 'signin' ? 'Signing in'
    : mode === 'signup' ? 'Building your board'
    : mode === 'finish' ? 'Building your board'
    : 'Sending'

  // Where the business questions belong, and where the provider buttons do not.
  const asksAboutBusiness = mode === 'signup' || mode === 'finish'
  const offersProviders = (google || apple || facebook) && (mode === 'signin' || mode === 'signup')

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={submit}>
        <div className="signin__brand">Boardium</div>
        <div className="signin__tag">Your board of directors.</div>

        {/* Takes the landing sheet's panel heading when it renders there. */}
        <h2 className="l-h2 l-auth__title">{heading}</h2>

        {error && <div className="signin__error">{error}</div>}
        {note && <div className="signin__note">{note}</div>}

        {unverified && (
          <button type="button" className="linkish signin__resend" onClick={resend} disabled={busy}>
            Send the confirmation link again
          </button>
        )}

        {/* The provider already told us who this is, and asking again invites a mismatch. */}
        {mode === 'finish' && (
          <p className="signin__note">
            Signed in with {pending?.provider === 'apple' ? 'Apple' : 'Google'}. Your board is
            built from these two answers, so they are worth getting right.
          </p>
        )}

        {mode !== 'finish' && (
          <label className="field">
            <span className="field__label">Email</span>
            <input className="input" type="email" autoComplete="username" required
                   value={email} onChange={e => setEmail(e.target.value)} />
          </label>
        )}

        {mode !== 'forgot' && mode !== 'finish' && (
          <label className="field">
            <span className="field__label">Password</span>
            <input
              className="input"
              type="password"
              /* Tells a password manager to offer a generated one on signup and the saved
                 one on sign-in. The same value on both makes it offer the wrong thing. */
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={mode === 'signup' ? 8 : undefined}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            {mode === 'signup' && <span className="field__hint">At least 8 characters.</span>}
          </label>
        )}

        {asksAboutBusiness && (
          <>
            <label className="field">
              <span className="field__label">Business name</span>
              <input className="input" type="text" autoComplete="organization" required
                     value={company} onChange={e => setCompany(e.target.value)} />
            </label>

            <label className="field">
              <span className="field__label">Trade</span>
              <input className="input" type="text" required placeholder="restaurant, plumbing, retail"
                     value={trade} onChange={e => setTrade(e.target.value)} />
              <span className="field__hint">
                What the business actually does. The board uses it from the first question.
              </span>
            </label>

            {/* The provider supplied a name already, so this would only be a chance to
                contradict it. */}
            {mode === 'signup' && (
              <label className="field">
                <span className="field__label">Your name</span>
                <input className="input" type="text" autoComplete="name"
                       value={name} onChange={e => setName(e.target.value)} />
              </label>
            )}
          </>
        )}

        <button className="btn btn--primary btn--block" disabled={busy}>
          {busy ? working : cta}
        </button>

        {offersProviders && (
          <div className="providers">
            <div className="providers__or">
              <span>{mode === 'signup' ? 'Or sign up with' : 'Or sign in with'}</span>
            </div>

            {/*
              Google's is a span, not a button, and that is deliberate. Google will only
              issue a credential from a button it drew itself, so its button is rendered
              into .providers__gsi at full width and laid over this face at opacity 0. There
              is exactly one control in this box and it belongs to Google; a second focusable
              element here would be a second tab stop that does nothing. The ring comes from
              :focus-within on the slot, because the button that has the focus is invisible.
            */}
            {google && (
              <div className="providers__slot" data-busy={busy ? 'true' : undefined}>
                <span className="providers__btn" aria-hidden="true">
                  <ProviderMark provider="google" />
                  Google
                </span>
                <div className="providers__gsi" ref={googleSlot} />
              </div>
            )}

            {facebook && (
              <button type="button" className="providers__btn" onClick={startFacebook} disabled={busy}>
                <ProviderMark provider="facebook" />
                Facebook
              </button>
            )}

            {/*
              Always drawn, never clickable yet. Apple has to be offered the day the iOS app
              ships, because an app carrying any other social sign-in is required to carry
              Apple's too, and a row that grows a third seat later is a row that has changed
              shape on people who had learnt it. `apple` is null in production today; when
              the Services ID is registered this becomes disabled={busy || !apple} and the
              handler underneath already works.

              The label is one word to match the other two. Apple's guidelines want "Sign in
              with Apple" on the button itself, so that is the thing to settle before this is
              switched on, not after.
            */}
            <button
              type="button"
              className="providers__btn"
              onClick={startApple}
              disabled
              title="Apple sign-in arrives with the iOS app"
            >
              <ProviderMark provider="apple" />
              Apple
            </button>
          </div>
        )}

        <div className="signin__switch">
          {mode === 'signin' && (
            <>
              {/*
                No way to open an account from inside the app, on purpose.

                Signing up here would work and would then land the new owner on a wall they
                cannot get past, because the trial takes a card and the card is taken on the
                website. Guideline 3.1.3(f) does not let the app point at that, so the app
                does not offer the half of the journey it cannot finish. Somebody who has an
                account signs in; App Review gets working credentials in the review notes.
              */}
              {!isNativeApp() && (
                <button type="button" className="linkish" onClick={() => go('signup')}>
                  Create an account
                </button>
              )}
              <button type="button" className="linkish" onClick={() => go('forgot')}>
                Forgot your password?
              </button>
            </>
          )}
          {mode === 'signup' && (
            <button type="button" className="linkish" onClick={() => go('signin')}>
              I already have an account
            </button>
          )}
          {mode === 'forgot' && (
            <button type="button" className="linkish" onClick={() => go('signin')}>
              Back to sign in
            </button>
          )}
          {mode === 'finish' && (
            <button type="button" className="linkish"
                    onClick={() => { setPending(null); go('signin') }}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

/**
 * Sign-in on its own, for the iOS app.
 *
 * The website answers / with the landing page, which argues for the product, names the trade
 * it is for and prints $249 on it. Inside the app that page is a call to action to purchase
 * outside the app, which is the one thing guideline 3.1.3(f) forbids, so the app never
 * renders it: a signed-out session lands straight on the card.
 *
 * The lockup is here rather than in the card because the card is shared with the website,
 * where the panel around it already carries the brand.
 */
export function AppSignIn({ onSignedIn }: { onSignedIn: (account: Account) => void }) {
  return (
    <div className="signin signin--app">
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div className="l-lockup" style={{ marginBottom: 'var(--space-5)' }}>
          <Mark className="l-lockup__mark" />
          <img className="l-lockup__word" src="/brand/wordmark-ink.png" alt="Boardium" />
        </div>
        <SignIn onSignedIn={onSignedIn} />
      </div>
    </div>
  )
}

/**
 * The provider marks, drawn rather than fetched.
 *
 * Now that the three buttons share one livery the mark is the only place a provider's brand
 * appears, so two of them keep their own colour: Google does not permit a monochrome G, and
 * a Facebook f that is not Facebook blue is just a blue-ish f. Apple's takes currentColor
 * instead, because the card goes navy in dark mode and a black mark would disappear into it.
 *
 * Inline because three <img> tags on the sign-in card are three requests that can arrive
 * after the buttons they belong to, and a button that grows a logo a moment later is worse
 * than one that never had one.
 */
function ProviderMark({ provider }: { provider: Provider }) {
  if (provider === 'google') {
    return (
      <svg className="providers__mark" viewBox="0 0 48 48" width="18" height="18" aria-hidden focusable="false">
        <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
        <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
        <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
        <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
      </svg>
    )
  }

  if (provider === 'facebook') {
    // #0866FF, which is Facebook's blue since 2023. The #1877F2 this used to carry is the
    // one before it, and next to the real thing it reads as a copy of the sign-in page.
    return (
      <svg className="providers__mark" viewBox="0 0 16 16" width="18" height="18" aria-hidden focusable="false">
        <circle cx="8" cy="8" r="8" fill="#0866FF" />
        <path
          fill="#fff"
          d="M11.11 10.31 11.47 8H9.25V6.51c0-.63.31-1.25 1.3-1.25h1.01V3.29s-.92-.16-1.79-.16c-1.82 0-3.02 1.11-3.02 3.11V8H4.72v2.31h2.03v5.59a8.1 8.1 0 0 0 2.5 0v-5.59h1.86z"
        />
      </svg>
    )
  }

  return (
    <svg className="providers__mark" viewBox="0 0 16 16" width="17" height="17" aria-hidden focusable="false">
      <path fill="currentColor" d="M11.18 8.5c.02-1.6 1.31-2.37 1.37-2.4-.75-1.1-1.91-1.25-2.33-1.27-.99-.1-1.93.58-2.43.58-.5 0-1.28-.57-2.1-.55-1.08.02-2.08.63-2.63 1.6-1.12 1.95-.29 4.83.81 6.41.54.77 1.18 1.64 2.02 1.61.81-.03 1.12-.52 2.1-.52.98 0 1.26.52 2.11.5.87-.01 1.42-.79 1.95-1.57.61-.9.87-1.77.88-1.81-.02-.01-1.69-.65-1.71-2.58zM9.6 3.24c.44-.54.74-1.28.66-2.02-.64.03-1.41.43-1.87.96-.41.48-.77 1.24-.67 1.97.71.05 1.44-.36 1.88-.91z" />
    </svg>
  )
}

/*
  The other end of the reset link. It is its own screen rather than a fourth mode above,
  because it is reached from an email with a token in the URL and has no business showing
  an email field at all: the token already says who this is.
*/
export function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (password !== again) { setError('Those two do not match.'); return }
    setBusy(true)
    setError(null)
    try {
      await endpoints.resetPassword(token, password)
      /*
        The endpoint signs them in as it goes, so a full navigation to / lands on the
        dashboard rather than the sign-in card. A route change would be neater, but the
        session lives in a component above this one and this screen is reached from an
        email rather than from inside the app, so there is nothing to hand it back to.

        It also drops the token out of the address bar, which matters: the URL is the
        credential and it should not sit in history any longer than it has to.
      */
      window.location.href = '/'
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set that password')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={submit}>
        <div className="signin__brand">Boardium</div>
        <div className="signin__tag">Set a new password.</div>

        <h2 className="l-h2 l-auth__title">Set a new password</h2>

        {!token && <div className="signin__error">That link is missing its token. Ask for a new one.</div>}
        {error && <div className="signin__error">{error}</div>}

        <label className="field">
          <span className="field__label">New password</span>
          <input className="input" type="password" autoComplete="new-password" required minLength={8}
                 value={password} onChange={e => setPassword(e.target.value)} />
          <span className="field__hint">At least 8 characters.</span>
        </label>

        <label className="field">
          <span className="field__label">Again</span>
          <input className="input" type="password" autoComplete="new-password" required
                 value={again} onChange={e => setAgain(e.target.value)} />
        </label>

        <button className="btn btn--primary btn--block" disabled={busy || !token}>
          {busy ? 'Setting it' : 'Set password and sign in'}
        </button>

        <div className="signin__switch">
          <a className="linkish" href="/">Back to sign in</a>
        </div>
      </form>
    </div>
  )
}

/*
  Where the confirmation link lands.

  It confirms on mount rather than showing a button: the person already clicked something to
  get here, and a second "yes I meant it" is a step that exists only to make the developer's
  life easier. The risk that normally justifies it, a mail scanner following the link and
  spending the token, costs nothing here — the worst case is an address confirmed slightly
  early by its own owner's server.
*/
export function VerifyEmail() {
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const [state, setState] = useState<'working' | 'done' | 'failed'>(token ? 'working' : 'failed')
  const [error, setError] = useState<string | null>(
    token ? null : 'That link is missing its token.')

  useEffect(() => {
    if (!token) return
    let live = true
    void endpoints.verifyEmail(token)
      .then(() => {
        if (!live) return
        setState('done')
        // Straight in. The endpoint signed them in as it went.
        setTimeout(() => { window.location.href = '/' }, 900)
      })
      .catch((err: unknown) => {
        if (!live) return
        setState('failed')
        setError(err instanceof Error ? err.message : 'That link did not work.')
      })
    return () => { live = false }
  }, [token])

  return (
    <div className="signin">
      <div className="signin__card">
        <div className="signin__brand">Boardium</div>
        <div className="signin__tag">Confirming your address.</div>

        <h2 className="l-h2 l-auth__title">
          {state === 'done' ? 'Confirmed' : state === 'failed' ? 'That link did not work' : 'One moment'}
        </h2>

        {state === 'working' && <p className="signin__note">Checking the link.</p>}
        {state === 'done' && <p className="signin__note">Taking you to your board.</p>}
        {state === 'failed' && error && <div className="signin__error">{error}</div>}

        {state === 'failed' && (
          <div className="signin__switch">
            <a className="linkish" href="/#signin">Go to sign in</a>
          </div>
        )}
      </div>
    </div>
  )
}

/*
  Where an invitation lands.

  It names the company before asking for anything, because "set a password" from an
  unfamiliar sender is indistinguishable from a phishing page. The address is fixed by the
  invitation and shown read-only: this screen cannot be used to join under a different one.
*/
export function JoinBoard() {
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const [invite, setInvite] = useState<{ email: string; company: string } | null>(null)
  const [checking, setChecking] = useState(true)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) { setChecking(false); return }
    let live = true
    void endpoints.peekInvite(token)
      .then(i => { if (live) setInvite(i) })
      .catch(() => { if (live) setInvite(null) })
      .finally(() => { if (live) setChecking(false) })
    return () => { live = false }
  }, [token])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    if (password !== again) { setError('Those two do not match.'); return }
    setBusy(true)
    setError(null)
    try {
      await endpoints.join(token, password, name.trim() || undefined)
      // Straight in. The endpoint signed them in, and the token leaves the address bar.
      window.location.href = '/'
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join')
    } finally {
      setBusy(false)
    }
  }

  if (checking) {
    return (
      <div className="signin">
        <div className="signin__card">
          <div className="signin__brand">Boardium</div>
          <p className="signin__note">Checking the invitation.</p>
        </div>
      </div>
    )
  }

  if (!invite) {
    return (
      <div className="signin">
        <div className="signin__card">
          <div className="signin__brand">Boardium</div>
          <h2 className="l-h2 l-auth__title">That invitation is not valid</h2>
          <div className="signin__error">
            It has expired, been withdrawn, or already been used. Ask whoever invited you to
            send another.
          </div>
          <div className="signin__switch">
            <a className="linkish" href="/#signin">Go to sign in</a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={submit}>
        <div className="signin__brand">Boardium</div>
        <div className="signin__tag">Join a board.</div>

        <h2 className="l-h2 l-auth__title">Join {invite.company}</h2>

        {error && <div className="signin__error">{error}</div>}

        <label className="field">
          <span className="field__label">Email</span>
          {/* Fixed by the invitation. Editable here would mean the link could be used to
              create an account under any address at all. */}
          <input className="input" type="email" value={invite.email} readOnly disabled />
        </label>

        <label className="field">
          <span className="field__label">Your name</span>
          <input className="input" type="text" autoComplete="name"
                 value={name} onChange={e => setName(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Set a password</span>
          <input className="input" type="password" autoComplete="new-password" required minLength={8}
                 value={password} onChange={e => setPassword(e.target.value)} />
          <span className="field__hint">At least 8 characters.</span>
        </label>

        <label className="field">
          <span className="field__label">Again</span>
          <input className="input" type="password" autoComplete="new-password" required
                 value={again} onChange={e => setAgain(e.target.value)} />
        </label>

        <button className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Joining' : `Join ${invite.company}`}
        </button>
      </form>
    </div>
  )
}
